import { useCallback } from 'react';
import omit from 'lodash/omit';
import useMonolithApiMutation, { UseMonolithApiMutationOptions } from '@/hooks/api/useMonolithApiMutation';
import { SimpleErrorResponse } from '@/models/Error';
import { scheduleBuildPath } from '@/utils/monolithApiRouteService';
import { HookInfo } from '@/models/HookInfo';
import { TriggerParams } from '@/models/TriggerParams';
import { getTimeFromHourAndMinute } from '../../AppDetails/components/BuildScheduleTime/utils/buildScheduleUtils';
import { ScheduleParams } from './types';

export type ScheduleBuildRequest = {
  buildTriggerToken: string;
  schedule: {
    id?: string;
    buildParams: {
      buildParams: TriggerParams;
      hookInfo: HookInfo;
      triggeredBy: 'curl';
    };
    isEnabled: boolean;
    time?: string;
    startDate?: string;
    setupLocation?: string;
    repetitionEnabled?: boolean;
    repeatEvery?: number;
    repeatInterval?: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
    monthlyRepetition?: 'day' | 'weekday';
    yearlyRepetition?: 'day' | 'weekday';
    weekdays?: number;
    cronExpression?: string;
  };
};

export type ScheduleBuildResponse = {
  info: {
    currentTime: string;
    location: string;
    scheduled: string;
    time: string;
    weekdays: number;
  };
};

type ScheduleBuildProps = {
  appSlug: string;
  buildTriggerToken: string;
};

const useScheduleBuild = (
  params: ScheduleBuildProps,
  options?: UseMonolithApiMutationOptions<ScheduleBuildResponse, SimpleErrorResponse, ScheduleBuildRequest>,
) => {
  const { appSlug, buildTriggerToken } = params;
  const { mutate, ...rest } = useMonolithApiMutation<ScheduleBuildResponse, SimpleErrorResponse, ScheduleBuildRequest>(
    scheduleBuildPath(appSlug),
    'post',
    options,
  );

  const newMutate = useCallback(
    (variables: { buildParams: TriggerParams; scheduleParams: ScheduleParams; id?: string; isEnabled?: boolean }) =>
      mutate({
        buildTriggerToken,
        schedule: {
          id: variables.id,
          buildParams: {
            buildParams: variables.buildParams,
            hookInfo: {
              buildTriggerToken,
              type: 'bitrise',
            },
            triggeredBy: 'curl',
          },
          isEnabled: !!variables.isEnabled,
          ...omit(variables.scheduleParams, ['hourAtSetupLocation', 'minuteAtSetupLocation']),
          time: getTimeFromHourAndMinute(
            variables.scheduleParams.hourAtSetupLocation ?? 0,
            variables.scheduleParams.minuteAtSetupLocation ?? 0,
          ),
        },
      }),
    [buildTriggerToken, mutate],
  );

  return {
    ...rest,
    mutate: newMutate,
  };
};

export default useScheduleBuild;
